"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;
    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);
      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38]  border-b border-[#b08d38] bg-white  " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;
        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300  ">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white  transition-transform duration-300 text-[#000]" +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%]  text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title:
        "The Mother Slab Waterproofing Begins with a 24-Hour Pre-Ponding Test",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Before any coating or garden preparation, we fill the terrace with
            water and let it stand for 24 hours. This helps us detect even the
            slightest leakage or seepage.
          </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            If dampness appears on the underside, our team carries out acrylic
            injection grouting - a method that seals micro-cracks from within
            the concrete. This ensures the slab is structurally sound and
            waterproof-ready.
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: "Dual-Layer Cement Polymer Modified Coating + Glass Fibre Mesh",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            On the cleaned slab, we apply two layers of a specialised cement
            polymer coating. This creates a flexible, crack-resistant barrier.
          </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Why it matters:
          </p>

          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Prevents cracks caused by weather changes",
              "Strengthens joints and corners",
              "Keeps water from penetrating concrete",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            We also embed Glass Fibre Mesh in critical joints to prevent future
            separation or cracking.
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: "A Protective Foam Concrete Layer (Durable Screed)",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Over the waterproofing, we lay a foam concrete screed.This layer:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Shields the waterproofing from damage",
              "Provides a solid base for the future garden",
              "Ensures long-term thermal stability",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This is a crucial step most terrace garden projects skip - but
            skipping it leads to future leaks.
          </p>
        </>
      ),
    },
    {
      id: 4,
      title: "A 72-Hour Post-Ponding Test: Triple-Checked for Perfection",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            After the screed sets, we repeat the testing - but this time for 72
            hours. This ensures:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {["No leaks", "No damp patches", "No weak spots"].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Until the terrace passes this test, we don’t move forward.
          </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Quality matters.
          </p>
        </>
      ),
    },
    {
      id: 5,
      title:
        "PU Primer + Polyurethane Waterproofing: Premium, UV-Resistant & Flexible",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            We then apply a polyurethane (PU)-based waterproofing system. PU
            waterproofing is considered the gold standard because:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "It stretches and adapts with temperature",
              "Resists UV rays",
              "Provides long-lasting waterproof performance",
              "Is seamless and joint-less",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This ensures your Sky Garden stays leak-free for years.
          </p>
        </>
      ),
    },
    {
      id: 6,
      title: "Engineered Slope + Drain Cells for Perfect Water Flow",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Water stagnation is the biggest enemy of terrace gardens. So we
            engineer the terrace with:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "A scientifically designed slope",
              "Drain cells that create a continuous water channel",
              "Easy water movement to outlets",
              "Zero stagnation or pooling",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This ensures your terrace remains dry and your waterproofing stays
            healthy.
          </p>
        </>
      ),
    },
    {
      id: 7,
      title: "Biofabric Layer to Protect Soil Nutrients",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Heavy rain can wash away soil and essential nutrients. Our biofabric
            layer:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Locks soil in place",
              "Prevents erosion",
              "Maintains moisture",
              "Keeps roots healthy",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This makes your garden grow effortlessly.
          </p>
        </>
      ),
    },
    {
      id: 8,
      title: "Built-In Irrigation, Concealed Plumbing & Lighting",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            A great terrace is one you can use - not one you struggle to
            maintain. So we integrate:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Irrigation lines",
              "Water outlets",
              "Lighting points",
              "Electrical conduits",
              "Concealed plumbing",
              "Space for comfortable seating",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            This means no renovation, no external contractors, no drilling or
            wiring after handover.
          </p>
        </>
      ),
    },
    {
      id: 9,
      title: "The Result? A Ready, Stress-Free Sky Garden from Day 1.",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            At +Willa, you’re not paying extra for this.
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              " You’re not spending months coordinating vendors.",
              " You’re not risking future leakage.",
              " You’re simply moving in - and enjoying the space from the very first day.",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            A Sky Garden that is:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Safe",
              "Leak-proof",
              "Durable",
              "Relaxing",
              "Low-maintenance",
              "Beautiful",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[6px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="black"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="black"
                    />
                  </svg>
                </span>
                <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
        </>
      ),
    },
  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};

export default function TerraceGardenSafe() {
  const data = [
    {
      image: `/images/blogs/terrace-garden-safe.webp`,
      title:
        "The Science Behind +Willa’s Sky Garden: How We Make Your Terrace Garden Safe, Durable & Ready from Day 1",
      date: "11 Dec 2025",

      content1: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            A private garden is one of the most cherished dreams of every villa
            buyer. A space to grow vegetables, sip coffee, read a book, or
            simply watch the sunrise. But the reality is that buying extra land
            for a garden is expensive - and in most villa communities, it isn’t
            even an option.
          </p>

          <p className="text-[18px] leading-[26px] mb-[20px] 2xl:text-[20px] 2xl:leading-[28px] font-medium text-gray-900">
            At +Willa, we took a different path.
          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Instead of searching for more land, we looked upward - to the
            terrace. One of the biggest, most underutilized spaces in any villa.
          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            But creating a terrace garden isn’t easy. Not if you want it to
            last.
          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Most buyers hesitate because a terrace garden comes with genuine
            challenges:
          </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            {[
              "Leakage due to poor waterproofing",
              "Drainage failures and water stagnation",
              "Cracks from thermal expansion",
              "Soil erosion due to rain",
              "No irrigation lines or lighting points",
              "No seating or usable layout",
            ].map((text, i) => (
              <li key={i} className="grid grid-cols-[30px_auto]">
                <span className="mt-[4px]">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none">
                    <path
                      d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                      fill="#171717"
                    />
                    <path
                      d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                      fill="#171717"
                    />
                  </svg>
                </span>
                <span className="text-[16px] leading-[24px]  text-[#171717]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 max-w-[80%] gap-[10px] my-[15px]">
            <div className="aspect-square overflow-hidden group">
              <Image
                src={`${ASSET_PREFIX}/images/blogs/science-behind-1.webp`}
                alt=""
                width={500}
                className=" group-hover:scale-[1.1] transition duration-300 ease-linear"
                height={500}></Image>
            </div>
            <div className="aspect-square overflow-hidden group">
              <Image
                src={`${ASSET_PREFIX}/images/blogs/science-behind-2.webp`}
                alt=""
                width={500}
                className=" group-hover:scale-[1.1] object-bottom  transition duration-300 ease-linear"
                height={500}></Image>
            </div>


          </div>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            This is why most terraces stay empty. The dream of a garden becomes
            a maintenance nightmare.
          </p>
          <p className="text-[18px] leading-[26px] mb-[20px] 2xl:text-[20px] 2xl:leading-[28px] font-medium text-gray-900">
            At +Willa, we solved this problem through engineering - not shortcut
            fixes.
          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Our Sky Garden is not an afterthought. It is an engineered system.
            Every villa comes with a fully prepared, waterproofed, long-lasting
            terrace garden base - ready from Day 1.
          </p>


          {/* <div className="grid grid-cols-2 max-w-[80%] gap-[10px] my-[15px]">

                      <div className="aspect-square overflow-hidden group">
                        <Image
                          src={`${ASSET_PREFIX}/images/blogs/science-behind-3.webp`}
                          alt=""
                          width={500}
                          className=" group-hover:scale-[1.1] transition duration-300 ease-linear"
                          height={500}></Image>
                      </div>
                      <div className="aspect-square overflow-hidden group">
                        <Image
                          src={`${ASSET_PREFIX}/images/blogs/science-behind-4.webp`}
                          alt=""
                          width={500}
                          className=" group-hover:scale-[1.1] transition duration-300 ease-linear"
                          height={500}></Image>
                      </div>
                    </div> */}
        </>
      ),
    },
  ];

  return (
    <div className="sticky top-[130px] z-[2] ">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog list"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px] ">
            <p className="my-[20px] text-[20px] leading-[28px] font-light max-xxl:text-[18px] max-xxl:leading-[26px] group-hover:text-[#8e722d] transition-colors duration-300 max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>

          <div className="p-[30px] bg-[#e8e8e8] rounded-[30px] mt-[50px]">
            <h2 className="text-[22px] leading-[30px] md:text-[24px] md:leading-[32px] 2xl:text-[26px] 2xl:leading-[34px] font-medium text-gray-900 mb-[15px]">
              Why We Do This
            </h2>
            <p className="mb-[15px]">
              Because villas today should do more than provide rooms.
            </p>
            <p className="mb-[15px]">They should provide experiences.</p>
            <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
              {[
                " Space to breathe.",
                " Space to grow.",
                " Space to live better.",
              ].map((text, i) => (
                <li key={i} className="grid grid-cols-[30px_auto]">
                  <span className="mt-[4px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="#171717"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="#171717"
                      />
                    </svg>
                  </span>
                  <span className=" ">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mb-[15px]">
              At +Willa, that’s the future we’re building.
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
